import { useEffect, useState } from "react";
import { CreditCard, CheckCircle, Trash, Warning, Shield } from "@phosphor-icons/react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function ClientPaymentMethod() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [pubKey, setPubKey] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients/me/payment-method");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    try {
      const { data } = await api.post("/clients/me/payment-method/setup-intent");
      setClientSecret(data.client_secret);
      if (data.publishable_key) {
        setPubKey(data.publishable_key);
        setStripePromise(loadStripe(data.publishable_key));
      } else {
        toast.error("Trainer hasn't configured Stripe publishable key — contact your coach");
        return;
      }
      setShowForm(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to start setup");
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your saved payment method? Your monthly auto-payment won't work until you add a new one.")) return;
    try {
      await api.delete("/clients/me/payment-method");
      toast.success("Payment method removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="client-payment-method-page">
      <div>
        <div className="tracker text-brand mb-3">[ Direct debit ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Payment method.</h1>
        <p className="text-zinc-500 mt-2 max-w-xl">
          Save a card on file so your monthly training payment is taken automatically on the 1st. No more manual invoices, no awkward &quot;did you pay?&quot; follow-ups.
        </p>
      </div>

      {loading ? (
        <div className="card-flat p-6 text-zinc-500" data-testid="pm-loading">Loading…</div>
      ) : status?.has_method ? (
        <div className="card-flat p-6 border-emerald-500/40" data-testid="pm-status-saved">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <CheckCircle size={32} weight="duotone" className="text-emerald-400" />
              <div>
                <div className="tracker text-emerald-400 mb-1">DIRECT DEBIT ACTIVE</div>
                <div className="font-mono text-lg">
                  {(status.brand || "card").toUpperCase()} •••• {status.last4 || "----"}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Saved {status.saved_at ? new Date(status.saved_at).toLocaleDateString() : ""}
                </div>
              </div>
            </div>
            <button onClick={remove} className="btn-ghost !py-2 !px-3 text-red-400" data-testid="pm-remove">
              <Trash size={14} weight="bold" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="card-flat p-8 text-center" data-testid="pm-status-empty">
          <CreditCard size={40} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
          <p className="text-zinc-400 mb-4">No payment method on file yet.</p>
          <button onClick={startSetup} className="btn-primary" data-testid="pm-setup-btn">
            <Shield size={14} weight="bold" /> Set up direct debit
          </button>
          <p className="text-[10px] text-zinc-600 mt-3 max-w-md mx-auto flex items-center justify-center gap-1.5">
            <Warning size={11} weight="bold" /> Stored securely by Stripe. Your coach never sees the full card number.
          </p>
        </div>
      )}

      {showForm && stripePromise && clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "night" } }}>
          <SetupForm
            onSuccess={() => { setShowForm(false); setClientSecret(null); load(); toast.success("Payment method saved"); }}
            onClose={() => { setShowForm(false); setClientSecret(null); }}
          />
        </Elements>
      )}
    </div>
  );
}

function SetupForm({ onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (error) {
      toast.error(error.message || "Card setup failed");
      setSubmitting(false);
      return;
    }
    if (setupIntent && setupIntent.payment_method) {
      try {
        await api.post("/clients/me/payment-method/save", {
          payment_method_id: setupIntent.payment_method,
        });
        onSuccess();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save method");
        setSubmitting(false);
      }
    } else {
      toast.error("Stripe didn't return a payment method");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="card-flat p-8 w-full max-w-lg my-10" data-testid="pm-setup-form">
        <div className="tracker text-brand mb-2">[ Direct debit ]</div>
        <h2 className="font-display text-3xl mb-6">Save your card</h2>
        <div className="mb-6">
          <PaymentElement />
        </div>
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={submitting} data-testid="pm-cancel">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting || !stripe} data-testid="pm-confirm">
            {submitting ? "Saving…" : "Save card"}
          </button>
        </div>
      </form>
    </div>
  );
}
